import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Info, ShieldCheck, X } from "lucide-react";
import './PopupProvider.css';

const PopupContext = createContext(null);

export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState(null);
  const active = useRef(null);
  const queue = useRef([]);
  const dialog = useRef(null);
  const sequence = useRef(0);

  const openPopup = useCallback((type, message, title, options = {}) => new Promise((resolve) => {
    const source = document.activeElement;
    const dark = document.documentElement.dataset.bqTheme === 'dark' || !!source?.closest('.bq-user-dark, .bq-admin-dark') ||
      (!source?.closest('.bq-user-light, .bq-admin-light') && !!document.querySelector('.bq-user-dark, .bq-admin-dark'));
    const config = { type, message, title, resolve, source, dark, id: ++sequence.current,
      variant: options.variant || (/delete|remove|reset|archive|decline/i.test(title) ? 'danger' : 'default'),
      confirmLabel: options.confirmLabel || (type === 'alert' ? 'OK' : 'Confirm'),
      cancelLabel: options.cancelLabel || 'Cancel' };
    if (active.current) queue.current.push(config);
    else { active.current = config; setPopup(config); }
  }), []);

  const showAlert = useCallback((message, title = 'Notice', options) => openPopup('alert', message, title, options), [openPopup]);
  const showConfirm = useCallback((message, title = 'Confirm action', options) => openPopup('confirm', message, title, options), [openPopup]);
  const closePopup = useCallback((result = false) => {
    const current = active.current;
    if (!current) return;
    active.current = queue.current.shift() || null;
    setPopup(active.current);
    current.resolve(result);
  }, []);

  useEffect(() => {
    if (!popup) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const panel = dialog.current;
    const focusables = () => [...panel.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), [tabindex="0"]')];
    // Start on Cancel for confirmations to avoid accidental destructive actions.
    (panel.querySelector('[data-popup-cancel]') || panel.querySelector('[data-popup-confirm]'))?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closePopup(false); }
      if (event.key !== 'Tab') return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey, true);
      if (popup.source?.isConnected) popup.source.focus();
    };
  }, [popup, closePopup]);

  useEffect(() => () => {
    active.current?.resolve(false);
    queue.current.forEach((item) => item.resolve(false));
  }, []);

  const Icon = popup?.variant === 'danger' ? AlertTriangle : popup?.type === 'confirm' ? ShieldCheck : Info;
  return (
    <PopupContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {popup && createPortal(
        <div className="bq-popup-overlay" data-theme={popup.dark ? 'dark' : 'light'}>
          <section ref={dialog} className={`bq-popup-panel${popup.variant === 'danger' ? ' is-danger' : ''}`}
            role={popup.type === 'confirm' ? 'alertdialog' : 'dialog'} aria-modal="true"
            aria-labelledby="bq-popup-title" aria-describedby="bq-popup-message">
            <button type="button" className="bq-popup-close" onClick={() => closePopup(false)} aria-label="Close dialog"><X size={18} /></button>
            <div className="bq-popup-heading">
              <span className="bq-popup-icon"><Icon size={22} strokeWidth={1.75} /></span>
              <div><p className="bq-popup-eyebrow">{popup.type === 'confirm' ? 'Please confirm' : 'Notification'}</p>
              <h2 id="bq-popup-title">{popup.title}</h2></div>
            </div>
            <p id="bq-popup-message" className="bq-popup-message">{popup.message}</p>
            <footer className="bq-popup-actions">
              {popup.type === 'confirm' && <button type="button" data-popup-cancel className="bq-popup-secondary" onClick={() => closePopup(false)}>{popup.cancelLabel}</button>}
              <button type="button" data-popup-confirm className="bq-popup-primary" onClick={() => closePopup(true)}>{popup.confirmLabel}</button>
            </footer>
          </section>
        </div>, document.body)}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) throw new Error('usePopup must be used within PopupProvider');
  return context;
};
